/**
 * Wizard Configuration Types
 * 
 * This file contains configuration-specific types and utilities
 * for setting up wizard instances.
 */

import type { WizardData, WizardConfig, WizardStep, ValidationResult } from './WizardTypes';

// ============================================================================
// Configuration Builder Types
// ============================================================================

/**
 * Step configuration builder for type-safe step definition
 */
export interface StepConfigBuilder<TData extends WizardData> {
  id: string;
  title: string;
  description?: string;
  component: React.ComponentType<import('./WizardTypes').WizardStepProps<TData>>;
  validation?: (data: TData) => ValidationResult;
  canSkip?: boolean;
  isOptional?: boolean;
}

/**
 * Wizard configuration builder for fluent API
 */
export class WizardConfigBuilder<TData extends WizardData> {
  private config: Partial<WizardConfig<TData>> = {
    steps: [],
    persistence: {
      autoSave: {
        enabled: true,
        intervalMs: 30000,
        onlyWhenDirty: true,
        saveOnStepChange: true,
      },
      draftKeyPrefix: 'wizard-draft',
      saveToDB: true,
      saveToLocalStorage: false,
    },
    validation: {
      validateOnChange: true,
      validateOnStepChange: true,
      showErrorsImmediately: false,
    },
  };

  constructor(id: string, title: string) {
    this.config.id = id;
    this.config.title = title;
  }

  description(desc: string): this {
    this.config.description = desc;
    return this;
  }

  initialData(data: TData): this {
    this.config.initialData = data;
    return this;
  }

  addStep(step: StepConfigBuilder<TData>): this {
    this.config.steps = [...(this.config.steps || []), step];
    return this;
  }

  addSteps(steps: StepConfigBuilder<TData>[]): this {
    this.config.steps = [...(this.config.steps || []), ...steps];
    return this;
  }

  autoSave(config: Partial<import('./WizardTypes').WizardAutoSaveConfig>): this {
    this.config.persistence = {
      ...this.config.persistence!,
      autoSave: { ...this.config.persistence!.autoSave, ...config },
    };
    return this;
  }

  persistence(config: Partial<import('./WizardTypes').WizardPersistenceConfig>): this {
    this.config.persistence = { ...this.config.persistence!, ...config };
    return this;
  }

  persistenceAdapter(adapter: import('../stores/useWizardPersistence').PersistenceService<TData>): this {
    // Store the adapter in the config for later use
    (this.config as Record<string, unknown>).persistenceAdapter = adapter;
    return this;
  }

  validation(config: Partial<import('./WizardTypes').WizardValidationConfig<TData>>): this {
    this.config.validation = { ...this.config.validation!, ...config };
    return this;
  }

  onSubmit(handler: (data: TData) => Promise<void>): this {
    this.config.onSubmit = handler;
    return this;
  }

  onCancel(handler: () => void): this {
    this.config.onCancel = handler;
    return this;
  }

  onStepChange(handler: (stepId: string, data: TData) => void): this {
    this.config.onStepChange = handler;
    return this;
  }

  build(): WizardConfig<TData> {
    if (!this.config.initialData) {
      throw new Error('Initial data is required for wizard configuration');
    }
    if (!this.config.onSubmit) {
      throw new Error('onSubmit handler is required for wizard configuration');
    }
    if (!this.config.steps || this.config.steps.length === 0) {
      throw new Error('At least one step is required for wizard configuration');
    }

    return this.config as WizardConfig<TData>;
  }
}

// ============================================================================
// Configuration Utilities
// ============================================================================

/**
 * Create a new wizard configuration builder
 */
export function createWizardConfig<TData extends WizardData>(
  id: string,
  title: string
): WizardConfigBuilder<TData> {
  return new WizardConfigBuilder<TData>(id, title);
}

/**
 * Create a step configuration
 */
export function createStep<TData extends WizardData>(
  config: StepConfigBuilder<TData>
): WizardStep<TData> {
  return {
    ...config,
    canSkip: config.canSkip ?? false,
    isOptional: config.isOptional ?? false,
  };
}

/**
 * Default validation function that always passes
 */
export const noValidation = (): ValidationResult => ({
  isValid: true,
  errors: [],
});

/**
 * Create a simple validation function from a predicate
 */
export function createValidation<TData extends WizardData>(
  predicate: (data: TData) => boolean,
  errorMessage: string
): (data: TData) => ValidationResult {
  return (data: TData) => ({
    isValid: predicate(data),
    errors: predicate(data) ? [] : [errorMessage],
  });
}

/**
 * Combine multiple validation functions
 */
export function combineValidations<TData extends WizardData>(
  ...validations: Array<(data: TData) => ValidationResult>
): (data: TData) => ValidationResult {
  return (data: TData) => {
    const results = validations.map(validation => validation(data));
    const allErrors = results.flatMap(result => result.errors);
    const allWarnings = results.flatMap(result => result.warnings || []);

    return {
      isValid: results.every(result => result.isValid),
      errors: allErrors,
      warnings: allWarnings.length > 0 ? allWarnings : undefined,
    };
  };
}

// ============================================================================
// Configuration Presets
// ============================================================================

/**
 * Default configuration presets for common wizard types
 */
export const WizardPresets = {
  /**
   * Quick wizard with minimal persistence and validation
   */
  quick: {
    persistence: {
      autoSave: {
        enabled: false,
        intervalMs: 0,
        onlyWhenDirty: false,
        saveOnStepChange: false,
      },
      draftKeyPrefix: 'quick-wizard',
      saveToDB: false,
      saveToLocalStorage: true,
    },
    validation: {
      validateOnChange: false,
      validateOnStepChange: true,
      showErrorsImmediately: true,
    },
  },

  /**
   * Form wizard with comprehensive persistence and validation
   */
  form: {
    persistence: {
      autoSave: {
        enabled: true,
        intervalMs: 15000,
        onlyWhenDirty: true,
        saveOnStepChange: true,
      },
      draftKeyPrefix: 'form-wizard',
      saveToDB: true,
      saveToLocalStorage: true,
    },
    validation: {
      validateOnChange: true,
      validateOnStepChange: true,
      showErrorsImmediately: false,
    },
  },

  /**
   * Complex workflow wizard with advanced features
   */
  workflow: {
    persistence: {
      autoSave: {
        enabled: true,
        intervalMs: 30000,
        onlyWhenDirty: true,
        saveOnStepChange: true,
      },
      draftKeyPrefix: 'workflow-wizard',
      saveToDB: true,
      saveToLocalStorage: false,
    },
    validation: {
      validateOnChange: true,
      validateOnStepChange: true,
      showErrorsImmediately: false,
    },
  },
} as const;
